import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Avatar,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Spinner,
  Alert,
  AlertIcon,
  Icon,
  Flex,
  useDisclosure,
  Divider,
  Select,
  Input,
  InputGroup,
  InputLeftElement
} from '@chakra-ui/react';
import {
  MdTrendingUp,
  MdAttachMoney,
  MdCheckCircle,
  MdAssignment,
  MdSearch,
  MdVisibility,
  MdSchedule,
  MdPerson
} from 'react-icons/md';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { getToken } from '../../../../services/api';
import PrecatorioInspector from './PrecatorioInspector';

const UserDetailModal = ({ isOpen, onClose, user }) => {
  const [userPerformance, setUserPerformance] = useState(null);
  const [userPrecatorios, setUserPrecatorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPrecatorio, setSelectedPrecatorio] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const { 
    isOpen: isInspectorOpen, 
    onOpen: onInspectorOpen, 
    onClose: onInspectorClose 
  } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    if (isOpen && user) {
      fetchUserDetails();
    }
  }, [isOpen, user]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;

      // Fetch performance data
      const perfResponse = await fetch(`${API_URL}/diretoria/team/${user._id}/performance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!perfResponse.ok) {
        throw new Error('Erro ao buscar performance');
      }

      const perfData = await perfResponse.json();
      setUserPerformance(perfData.data);

      // Fetch precatorios
      const precResponse = await fetch(`${API_URL}/diretoria/team/${user._id}/precatorios`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!precResponse.ok) {
        throw new Error('Erro ao buscar precatórios');
      }

      const precData = await precResponse.json();
      setUserPrecatorios(precData.data.precatorios || []);
      
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPrecatorio = (precatorio) => {
    setSelectedPrecatorio(precatorio);
    onInspectorOpen();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getStatusLabel = (status) => {
    const labels = {
      'novo': 'Novo',
      'solicitacao_proposta': 'Solicitação de Proposta',
      'proposta_aceita': 'Proposta Aceita',
      'proposta_negada': 'Proposta Negada',
      'solicitacao_documentos': 'Solicitação de Documentos',
      'documentos_completos': 'Documentos Completos',
      'em_analise': 'Em Análise',
      'fechado': 'Fechado'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'novo': 'blue',
      'solicitacao_proposta': 'purple',
      'proposta_aceita': 'green',
      'proposta_negada': 'red',
      'solicitacao_documentos': 'orange',
      'documentos_completos': 'yellow',
      'em_analise': 'cyan',
      'fechado': 'green'
    };
    return colors[status] || 'gray';
  };

  // Prepare data for pie chart
  const prepareChartData = () => {
    if (!userPerformance) return [];
    
    const statusBreakdown = userPerformance.metrics.total.statusBreakdown;
    return Object.entries(statusBreakdown).map(([status, count]) => ({
      name: getStatusLabel(status),
      value: count,
      color: getChartColor(status)
    }));
  };

  const getChartColor = (status) => {
    const colors = {
      'novo': '#3182ce',
      'solicitacao_proposta': '#805ad5',
      'proposta_aceita': '#38a169',
      'proposta_negada': '#e53e3e',
      'solicitacao_documentos': '#dd6b20',
      'documentos_completos': '#d69e2e',
      'em_analise': '#00b5d8',
      'fechado': '#22543d'
    };
    return colors[status] || '#718096';
  };

  // Filter precatorios
  const filteredPrecatorios = userPrecatorios.filter(p => {
    const matchesStatus = !filterStatus || p.status === filterStatus;
    const matchesSearch = !searchTerm || 
      p.numero_precatorio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.document_used?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getDaysInStatus = (lastUpdate) => {
    if (!lastUpdate) return 0;
    const diff = Date.now() - new Date(lastUpdate).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxW="90vw">
          <ModalHeader>
            <HStack spacing={4}>
              <Avatar size="lg" name={user.name} />
              <VStack align="start" spacing={0}>
                <Text fontSize="xl">{user.name}</Text>
                <Text fontSize="sm" color="gray.500">{user.email}</Text>
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            {loading ? (
              <Flex justify="center" align="center" h="400px">
                <Spinner size="xl" />
              </Flex>
            ) : error ? (
              <Alert status="error">
                <AlertIcon />
                {error}
              </Alert>
            ) : (
              <Tabs>
                <TabList>
                  <Tab>Visão Geral</Tab>
                  <Tab>Precatórios ({filteredPrecatorios.length})</Tab>
                  <Tab>Performance</Tab>
                </TabList>

                <TabPanels>
                  {/* Overview Tab */}
                  <TabPanel>
                    <VStack spacing={6} align="stretch">
                      {/* Key Metrics */}
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                        <Box bg={tableBg} p={4} borderRadius="md">
                          <Stat>
                            <StatLabel>Total Precatórios</StatLabel>
                            <StatNumber>{userPerformance?.metrics.total.precatorios || 0}</StatNumber>
                            <StatHelpText>Em gerenciamento</StatHelpText>
                          </Stat>
                        </Box>

                        <Box bg={tableBg} p={4} borderRadius="md">
                          <Stat>
                            <StatLabel>Valor Total</StatLabel>
                            <StatNumber fontSize="lg">
                              {formatCurrency(userPerformance?.metrics.total.valorTotal || 0)}
                            </StatNumber>
                            <StatHelpText>Sob gestão</StatHelpText>
                          </Stat>
                        </Box>

                        <Box bg={tableBg} p={4} borderRadius="md">
                          <Stat>
                            <StatLabel>Taxa de Sucesso</StatLabel>
                            <StatNumber>{userPerformance?.metrics.total.successRate || 0}%</StatNumber>
                            <StatHelpText>
                              {userPerformance?.metrics.total.statusBreakdown?.fechado || 0} fechados
                            </StatHelpText>
                          </Stat>
                        </Box>

                        <Box bg={tableBg} p={4} borderRadius="md">
                          <Stat>
                            <StatLabel>Taxa de Aceitação</StatLabel>
                            <StatNumber>{userPerformance?.metrics.total.acceptanceRate || 0}%</StatNumber>
                            <StatHelpText>Propostas aceitas</StatHelpText>
                          </Stat>
                        </Box>
                      </SimpleGrid>

                      {/* Status Distribution Chart */}
                      {!isInspectorOpen && (
                        <Box bg={tableBg} p={4} borderRadius="md">
                          <Text fontWeight="bold" mb={4}>Distribuição por Status</Text>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={prepareChartData()}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={(entry) => `${entry.name}: ${entry.value}`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {prepareChartData().map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                      )}

                      {/* Performance by State */}
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        {userPerformance?.metrics.byState && Object.entries(userPerformance.metrics.byState).map(([state, metrics]) => (
                          <Box key={state} bg={tableBg} p={4} borderRadius="md">
                            <Text fontWeight="bold" mb={2}>Estado: {state}</Text>
                            <VStack align="stretch" spacing={2}>
                              <HStack justify="space-between">
                                <Text fontSize="sm">Total:</Text>
                                <Text fontSize="sm" fontWeight="bold">{metrics.total}</Text>
                              </HStack>
                              <HStack justify="space-between">
                                <Text fontSize="sm">Valor Total:</Text>
                                <Text fontSize="sm" fontWeight="bold">
                                  {formatCurrency(metrics.valorTotal)}
                                </Text>
                              </HStack>
                              <HStack justify="space-between">
                                <Text fontSize="sm">Documentos Completos:</Text>
                                <Text fontSize="sm" fontWeight="bold">
                                  {metrics.documentCompletionRate}%
                                </Text>
                              </HStack>
                              {metrics.averageTimeToClose > 0 && (
                                <HStack justify="space-between">
                                  <Text fontSize="sm">Tempo Médio p/ Fechar:</Text>
                                  <Text fontSize="sm" fontWeight="bold">
                                    {metrics.averageTimeToClose} dias
                                  </Text>
                                </HStack>
                              )}
                            </VStack>
                          </Box>
                        ))}
                      </SimpleGrid>
                    </VStack>
                  </TabPanel>

                  {/* Precatorios Tab */}
                  <TabPanel>
                    <VStack spacing={4} align="stretch">
                      {/* Filters */}
                      <HStack spacing={4}>
                        <InputGroup maxW="300px">
                          <InputLeftElement pointerEvents="none">
                            <MdSearch color="gray.300" />
                          </InputLeftElement>
                          <Input
                            placeholder="Buscar precatório..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </InputGroup>

                        <Select
                          maxW="200px"
                          placeholder="Todos os status"
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                        >
                          <option value="novo">Novo</option>
                          <option value="solicitacao_proposta">Solicitação de Proposta</option>
                          <option value="proposta_aceita">Proposta Aceita</option>
                          <option value="proposta_negada">Proposta Negada</option>
                          <option value="solicitacao_documentos">Solicitação de Documentos</option>
                          <option value="documentos_completos">Documentos Completos</option>
                          <option value="em_analise">Em Análise</option>
                          <option value="fechado">Fechado</option>
                        </Select>
                      </HStack>

                      {/* Precatorios Table */}
                      <Box overflowX="auto">
                        <Table variant="simple">
                          <Thead bg={tableBg}>
                            <Tr>
                              <Th>Estado</Th>
                              <Th>Número</Th>
                              <Th>Documento</Th>
                              <Th>Nome</Th>
                              <Th>Status</Th>
                              <Th>Dias no Status</Th>
                              <Th>Valor</Th>
                              <Th>Última Ação</Th>
                              <Th>Ações</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {filteredPrecatorios.map((precatorio) => (
                              <Tr key={precatorio._id}>
                                <Td>
                                  <Badge>{precatorio.state}</Badge>
                                </Td>
                                <Td fontWeight="medium">{precatorio.numero_precatorio}</Td>
                                <Td>{precatorio.document_used}</Td>
                                <Td>{precatorio.nome}</Td>
                                <Td>
                                  <Badge colorScheme={getStatusColor(precatorio.status)}>
                                    {getStatusLabel(precatorio.status)}
                                  </Badge>
                                </Td>
                                <Td>{getDaysInStatus(precatorio.data_ultima_atualizacao)}</Td>
                                <Td>{formatCurrency(precatorio.valor_atualizado || 0)}</Td>
                                <Td>{formatDate(precatorio.data_ultima_atualizacao)}</Td>
                                <Td>
                                  <Button
                                    size="sm"
                                    leftIcon={<MdVisibility />}
                                    onClick={() => handleViewPrecatorio(precatorio)}
                                    colorScheme="blue"
                                    variant="ghost"
                                  >
                                    Ver
                                  </Button>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>

                        {filteredPrecatorios.length === 0 && (
                          <Box textAlign="center" py={10}>
                            <Text color="gray.500">Nenhum precatório encontrado</Text>
                          </Box>
                        )}
                      </Box>
                    </VStack>
                  </TabPanel>

                  {/* Performance Tab */}
                  <TabPanel>
                    <VStack spacing={6} align="stretch">
                      {/* Success Metrics */}
                      <Box bg={tableBg} p={4} borderRadius="md">
                        <Text fontWeight="bold" mb={4}>Métricas de Sucesso</Text>
                        
                        <VStack spacing={4} align="stretch">
                          <Box>
                            <HStack justify="space-between" mb={2}>
                              <Text>Taxa de Sucesso (Fechados)</Text>
                              <Text fontWeight="bold">
                                {userPerformance?.metrics.total.successRate || 0}%
                              </Text>
                            </HStack>
                            <Progress 
                              value={userPerformance?.metrics.total.successRate || 0} 
                              colorScheme="green"
                            />
                          </Box>

                          <Box>
                            <HStack justify="space-between" mb={2}>
                              <Text>Taxa de Aceitação de Propostas</Text>
                              <Text fontWeight="bold">
                                {userPerformance?.metrics.total.acceptanceRate || 0}%
                              </Text>
                            </HStack>
                            <Progress 
                              value={userPerformance?.metrics.total.acceptanceRate || 0} 
                              colorScheme="blue"
                            />
                          </Box>
                        </VStack>
                      </Box>

                      {/* Financial Summary */}
                      <Box bg={tableBg} p={4} borderRadius="md">
                        <Text fontWeight="bold" mb={4}>Resumo Financeiro</Text>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                          <Stat>
                            <StatLabel>Valor Total Gerenciado</StatLabel>
                            <StatNumber fontSize="lg">
                              {formatCurrency(userPerformance?.metrics.total.valorTotal || 0)}
                            </StatNumber>
                          </Stat>
                          <Stat>
                            <StatLabel>Valor em Propostas Aceitas</StatLabel>
                            <StatNumber fontSize="lg">
                              {formatCurrency(userPerformance?.metrics.total.valorPropostas || 0)}
                            </StatNumber>
                          </Stat>
                          <Stat>
                            <StatLabel>Média por Precatório</StatLabel>
                            <StatNumber fontSize="lg">
                              {formatCurrency(
                                userPerformance?.metrics.total.precatorios > 0
                                  ? userPerformance.metrics.total.valorTotal / userPerformance.metrics.total.precatorios
                                  : 0
                              )}
                            </StatNumber>
                          </Stat>
                        </SimpleGrid>
                      </Box>

                      {/* Activity Summary */}
                      <Box bg={tableBg} p={4} borderRadius="md">
                        <Text fontWeight="bold" mb={4}>Resumo de Atividades</Text>
                        <VStack align="stretch" spacing={2}>
                          <HStack justify="space-between">
                            <Text>Precatórios Novos:</Text>
                            <Badge colorScheme="blue">
                              {userPerformance?.metrics.total.statusBreakdown?.novo || 0}
                            </Badge>
                          </HStack>
                          <HStack justify="space-between">
                            <Text>Em Negociação:</Text>
                            <Badge colorScheme="purple">
                              {userPerformance?.metrics.total.statusBreakdown?.solicitacao_proposta || 0}
                            </Badge>
                          </HStack>
                          <HStack justify="space-between">
                            <Text>Aguardando Documentos:</Text>
                            <Badge colorScheme="orange">
                              {userPerformance?.metrics.total.statusBreakdown?.solicitacao_documentos || 0}
                            </Badge>
                          </HStack>
                          <HStack justify="space-between">
                            <Text>Em Análise:</Text>
                            <Badge colorScheme="cyan">
                              {userPerformance?.metrics.total.statusBreakdown?.em_analise || 0}
                            </Badge>
                          </HStack>
                          <HStack justify="space-between">
                            <Text>Fechados:</Text>
                            <Badge colorScheme="green">
                              {userPerformance?.metrics.total.statusBreakdown?.fechado || 0}
                            </Badge>
                          </HStack>
                        </VStack>
                      </Box>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>

          <ModalFooter>
            <Button onClick={onClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Precatorio Inspector Modal */}
      {selectedPrecatorio && (
        <PrecatorioInspector
          isOpen={isInspectorOpen}
          onClose={onInspectorClose}
          precatorio={selectedPrecatorio}
        />
      )}
    </>
  );
};

export default UserDetailModal;